import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Container,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';
import { formatearFechaBD as formatFechaAR } from '../../utils/reciboUtils';

function PanelAdmin() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados para búsqueda/filtro
  const [busquedaEmpleados, setBusquedaEmpleados] = useState('');
  const [busquedaCargarVacaciones, setBusquedaCargarVacaciones] = useState('');
  const [mostrarDropdownBusqueda, setMostrarDropdownBusqueda] = useState(false);
  const [busquedaSolicitudes, setBusquedaSolicitudes] = useState('');

  // Estados para Empleados
  const [empleados, setEmpleados] = useState([]);
  const [anioEmpleados, setAnioEmpleados] = useState(new Date().getFullYear());

  // Estados para Solicitudes
  const [solicitudes, setSolicitudes] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());

  // Estados para Diálogos
  const [openEditarDias, setOpenEditarDias] = useState(false);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [diasAjuste, setDiasAjuste] = useState(0);
  const [motivoAjuste, setMotivoAjuste] = useState('');

  const [openCargarVacaciones, setOpenCargarVacaciones] = useState(false);
  const [nuevaVacacion, setNuevaVacacion] = useState({
    usuario_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: ''
  });

  const [openDetalleSolicitud, setOpenDetalleSolicitud] = useState(false);
  const [solicitudDetalle, setSolicitudDetalle] = useState(null);

  const [openCancelarSolicitud, setOpenCancelarSolicitud] = useState(false);
  const [solicitudACancelar, setSolicitudACancelar] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');

  const [openSuspenderSolicitud, setOpenSuspenderSolicitud] = useState(false);
  const [solicitudASuspender, setSolicitudASuspender] = useState(null);
  const [diasADevolver, setDiasADevolver] = useState('');
  const [motivoSuspension, setMotivoSuspension] = useState('');

  useEffect(() => {
    if (tabValue === 0) {
      cargarEmpleados();
    } else if (tabValue === 1) {
      // Tab de cargar vacaciones necesita lista de empleados
      if (empleados.length === 0) {
        cargarEmpleados();
      }
    } else if (tabValue === 2) {
      cargarSolicitudes();
    }
  }, [tabValue, anioEmpleados, filtroEstado, filtroAnio]);

  // ========== FUNCIONES PARA EMPLEADOS ==========

  const cargarEmpleados = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/todos-empleados?anio=${anioEmpleados}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error('Error al cargar empleados');
      const data = await res.json();
      setEmpleados(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditarDias = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setDiasAjuste(0);
    setMotivoAjuste('');
    setOpenEditarDias(true);
  };

  const handleGuardarAjusteDias = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/editar-dias/${empleadoSeleccionado.usuario_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          dias_ajuste: parseInt(diasAjuste),
          motivo: motivoAjuste,
          anio: anioEmpleados
        })
      });

      if (!res.ok) throw new Error('Error al ajustar días');

      const data = await res.json();
      setSuccess(`Días ajustados exitosamente para ${empleadoSeleccionado.nombre} ${empleadoSeleccionado.apellido}`);
      setOpenEditarDias(false);
      cargarEmpleados();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========== FUNCIONES PARA CARGAR VACACIONES ==========

  const handleCargarVacaciones = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!nuevaVacacion.usuario_id || !nuevaVacacion.fecha_inicio || !nuevaVacacion.fecha_fin) {
        setError('Todos los campos son obligatorios');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/cargar-vacaciones`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(nuevaVacacion)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Error al cargar vacaciones');
      }

      const data = await res.json();
      setSuccess(`Vacaciones cargadas exitosamente (${data.dias_solicitados} días)`);
      setOpenCargarVacaciones(false);
      setNuevaVacacion({
        usuario_id: '',
        fecha_inicio: '',
        fecha_fin: '',
        comentarios: ''
      });
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========== FUNCIONES PARA SOLICITUDES ==========

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `${API_BASE_URL}/api/vacaciones/admin/todas-solicitudes?`;
      if (filtroEstado) url += `estado=${filtroEstado}&`;
      if (filtroAnio) url += `anio=${filtroAnio}`;

      const res = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error('Error al cargar solicitudes');
      const data = await res.json();
      setSolicitudes(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = (solicitud) => {
    setSolicitudDetalle(solicitud);
    setOpenDetalleSolicitud(true);
  };

  // ========== FUNCIONES PARA CANCELAR / SUSPENDER SOLICITUDES APROBADAS ==========

  const handleAbrirCancelar = (solicitud) => {
    setSolicitudACancelar(solicitud);
    setMotivoCancelacion('');
    setOpenCancelarSolicitud(true);
  };

  const handleConfirmarCancelar = async () => {
    if (!solicitudACancelar || !motivoCancelacion.trim()) {
      setError('El motivo es obligatorio');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/cancelar/${solicitudACancelar.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivoCancelacion })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cancelar la solicitud');

      setSuccess(`Solicitud cancelada. Se devolvieron ${data.dias_devueltos} días al saldo del empleado.`);
      setOpenCancelarSolicitud(false);
      setSolicitudACancelar(null);
      cargarSolicitudes();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirSuspender = (solicitud) => {
    setSolicitudASuspender(solicitud);
    setDiasADevolver('');
    setMotivoSuspension('');
    setOpenSuspenderSolicitud(true);
  };

  const handleConfirmarSuspender = async () => {
    if (!solicitudASuspender || !motivoSuspension.trim() || !diasADevolver) {
      setError('Los días a devolver y el motivo son obligatorios');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/suspender/${solicitudASuspender.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dias_a_devolver: parseInt(diasADevolver), motivo: motivoSuspension })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al suspender la solicitud');

      setSuccess(`Solicitud suspendida. Se devolvieron ${data.dias_devueltos} días al saldo del empleado (gozó ${data.dias_gozados}).`);
      setOpenSuspenderSolicitud(false);
      setSolicitudASuspender(null);
      cargarSolicitudes();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========== FUNCIONES PARA GESTIÓN DE PERÍODOS ==========

  const handleDescargarPlantilla = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/descargar-plantilla`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Error al descargar plantilla');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_vacaciones_${new Date().getFullYear()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess('Plantilla Excel descargada. Completá la columna "Dias Acumulados Periodo Anterior" y cargá el archivo.');
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCargarArchivoDiasAcumulados = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      setLoading(true);
      setError(null);

      // Leer archivo Excel
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Obtener la primera hoja
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const excelData = XLSX.utils.sheet_to_json(firstSheet);
      
      // Procesar datos
      const empleados = [];
      for (const row of excelData) {
        const id = parseInt(row['ID']);
        const diasAcumulados = parseInt(row['Dias Acumulados Periodo Anterior']) || 0;
        
        if (!isNaN(id)) {
          empleados.push({
            id,
            dias_acumulados_periodo_anterior: diasAcumulados
          });
        }
      }

      if (empleados.length === 0) {
        setError('No se encontraron empleados válidos en el archivo');
        return;
      }

      // Enviar al backend
      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/cargar-dias-acumulados`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empleados,
          anio: anioEmpleados
        })
      });

      if (!res.ok) throw new Error('Error al cargar días acumulados');

      const resultado = await res.json();
      setSuccess(`${resultado.message}${resultado.errores ? ` (${resultado.errores.length} errores)` : ''}`);
      
      if (resultado.errores && resultado.errores.length > 0) {
        console.warn('Errores en la carga:', resultado.errores);
      }

      cargarEmpleados();
      
      // Limpiar input
      event.target.value = '';
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInicializarPeriodo = async () => {
    try {
      if (!window.confirm(`¿Estás seguro de inicializar el período ${anioEmpleados}? Esta acción no se puede deshacer.`)) {
        return;
      }

      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/inicializar-periodo`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          anio: anioEmpleados
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Error al inicializar período');
      }

      const data = await res.json();
      setSuccess(`${data.message} - ${data.empleados_procesados} empleados procesados`);
      cargarEmpleados();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInicializarPendientes = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/admin/inicializar-pendientes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          anio: anioEmpleados
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Error al inicializar empleados pendientes');
      }

      setSuccess(data.message);
      cargarEmpleados();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ========== UTILIDADES ==========

  // Los ajustes administrativos de días (Gestión Administrativa > lápiz)
  // se guardan como una fila más en vacaciones_solicitadas para que el
  // saldo se recalcule solo, pero no son una vacación real: no tienen
  // que confundirse con un "Aprobado" de verdad en esta tabla.
  const esAjusteAdministrativo = (sol) =>
    typeof sol?.comentarios_empleado === 'string' && sol.comentarios_empleado.startsWith('[AJUSTE ADMINISTRATIVO]');

  const getEstadoChip = (estado, esAjuste = false) => {
    if (esAjuste && estado === 'aprobado') {
      return <Chip label="🔧 Ajuste de días" color="secondary" size="small" variant="outlined" />;
    }

    const config = {
      'pendiente_referente': { label: '⏳ Pendiente Referente', color: 'warning' },
      'pendiente_rh': { label: '⏳ Pendiente RH', color: 'info' },
      'aprobado': { label: '✅ Aprobado', color: 'success' },
      'rechazado_referente': { label: '❌ Rechazado (Ref)', color: 'error' },
      'rechazado_rh': { label: '❌ Rechazado (RH)', color: 'error' },
      'cancelado': { label: '⊘ Cancelado', color: 'default' },
      'suspendido': { label: '⏸ Suspendido', color: 'warning' }
    };

    const { label, color } = config[estado] || { label: estado, color: 'default' };
    return <Chip label={label} color={color} size="small" />;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return formatFechaAR(fecha);
  };

  // ========== RENDER ==========

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Administración Vacaciones
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AssessmentIcon />}
          onClick={() => navigate('/vacaciones/reporte-mes')}
        >
          📊 Reporte por Mes
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Empleados y Días" />
          <Tab label="Cargar Vacaciones" />
          <Tab label="Todas las Solicitudes" />
        </Tabs>
      </Paper>

      {/* TAB 0: EMPLEADOS Y DÍAS DISPONIBLES */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Empleados y Días Disponibles</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Año</InputLabel>
                <Select
                  value={anioEmpleados}
                  label="Año"
                  onChange={(e) => setAnioEmpleados(e.target.value)}
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 3 + i).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={cargarEmpleados} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Botones de gestión de períodos */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDescargarPlantilla}
              disabled={loading}
            >
              📥 Descargar Plantilla Empleados
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component="label"
              disabled={loading}
            >
              📤 Cargar Días Acumulados
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleCargarArchivoDiasAcumulados}
              />
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleInicializarPeriodo}
              disabled={loading}
            >
              🚀 Inicializar Nuevo Período
            </Button>
            <Tooltip title="Agrega al período ya inicializado a los empleados que faltan (ej. ingresaron después de correr 'Inicializar Nuevo Período'), sin tocar a los que ya lo tienen cargado">
              <Button
                variant="outlined"
                color="success"
                onClick={handleInicializarPendientes}
                disabled={loading}
              >
                ➕ Agregar Empleados Nuevos al Período
              </Button>
            </Tooltip>
          </Box>

          {/* Buscador */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por legajo, DNI o apellido"
              value={busquedaEmpleados}
              onChange={(e) => setBusquedaEmpleados(e.target.value)}
              placeholder="Ingrese legajo, DNI o apellido..."
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Empleado</TableCell>
                    <TableCell>Legajo</TableCell>
                    <TableCell>DNI</TableCell>
                    <TableCell align="center">Fecha Antigüedad</TableCell>
                    <TableCell align="center">Estado Período</TableCell>
                    <TableCell align="center">Correspondientes</TableCell>
                    <TableCell align="center">Acumulados</TableCell>
                    <TableCell align="center">Tomados</TableCell>
                    <TableCell align="center">Pendientes</TableCell>
                    <TableCell align="center">Disponibles</TableCell>
                    <TableCell align="center">Solicitudes Pendientes</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {empleados
                    .filter(emp => {
                      if (!busquedaEmpleados) return true;
                      const termino = busquedaEmpleados.toLowerCase();
                      return (
                        (emp.numero_legajo && emp.numero_legajo.toString().toLowerCase().includes(termino)) ||
                        (emp.dni && emp.dni.toString().toLowerCase().includes(termino)) ||
                        (emp.apellido && emp.apellido.toLowerCase().includes(termino)) ||
                        (emp.nombre && emp.nombre.toLowerCase().includes(termino))
                      );
                    })
                    .map((emp) => (
                    <TableRow key={emp.usuario_id}>
                      <TableCell>{emp.apellido}, {emp.nombre}</TableCell>
                      <TableCell>{emp.numero_legajo || '-'}</TableCell>
                      <TableCell>{emp.dni}</TableCell>
                      <TableCell align="center">
                        {emp.fecha_base_antiguedad
                          ? formatFechaAR(emp.fecha_base_antiguedad)
                          : emp.fecha_ingreso
                            ? formatFechaAR(emp.fecha_ingreso)
                            : '-'
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={emp.periodo_inicializado ? 'Habilitado' : 'No inicializado'}
                          color={emp.periodo_inicializado ? 'success' : 'default'}
                          size="small"
                          variant={emp.periodo_inicializado ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">{emp.dias_correspondientes}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={emp.dias_acumulados || 0} 
                          color={emp.dias_acumulados > 0 ? 'info' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="center">{emp.dias_tomados}</TableCell>
                      <TableCell align="center">{emp.dias_pendientes}</TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: emp.dias_disponibles < 0 ? 'error.main' : 'success.main'
                          }}
                        >
                          {emp.dias_disponibles}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {emp.solicitudes_pendientes > 0 ? (
                          <Chip label={emp.solicitudes_pendientes} color="warning" size="small" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ajustar días disponibles">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditarDias(emp)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* TAB 1: CARGAR VACACIONES */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Cargar Vacaciones Manualmente</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Esta funcionalidad permite cargar vacaciones directamente como aprobadas, sin pasar por el flujo de aprobación normal.
          </Typography>

          {/* Buscador con resultados */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <TextField
              fullWidth
              size="small"
              label="Buscar empleado por legajo, DNI o apellido"
              value={busquedaCargarVacaciones}
              onChange={(e) => {
                setBusquedaCargarVacaciones(e.target.value);
                setMostrarDropdownBusqueda(true);
              }}
              onFocus={() => setMostrarDropdownBusqueda(true)}
              placeholder="Ingrese legajo, DNI o apellido..."
            />

            {/* Lista de resultados */}
            {busquedaCargarVacaciones && mostrarDropdownBusqueda && (
              <Paper 
                sx={{ 
                  position: 'absolute', 
                  width: '100%', 
                  maxHeight: 300, 
                  overflow: 'auto', 
                  zIndex: 1000,
                  mt: 0.5
                }}
              >
                {empleados
                  .filter(emp => {
                    const termino = busquedaCargarVacaciones.toLowerCase();
                    return (
                      (emp.numero_legajo && emp.numero_legajo.toString().toLowerCase().includes(termino)) ||
                      (emp.dni && emp.dni.toString().toLowerCase().includes(termino)) ||
                      (emp.apellido && emp.apellido.toLowerCase().includes(termino)) ||
                      (emp.nombre && emp.nombre.toLowerCase().includes(termino))
                    );
                  })
                  .map((emp) => (
                    <Box
                      key={emp.usuario_id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        '&:hover': { bgcolor: '#f5f5f5' },
                        bgcolor: nuevaVacacion.usuario_id === emp.usuario_id ? '#e3f2fd' : 'transparent'
                      }}
                      onClick={() => {
                        setNuevaVacacion({ ...nuevaVacacion, usuario_id: emp.usuario_id });
                        setBusquedaCargarVacaciones(`${emp.apellido}, ${emp.nombre} - DNI: ${emp.dni}`);
                        setMostrarDropdownBusqueda(false);
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{emp.apellido}, {emp.nombre}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        DNI: {emp.dni} - Leg: {emp.numero_legajo || 'S/N'}
                      </Typography>
                    </Box>
                  ))}
                {empleados.filter(emp => {
                  const termino = busquedaCargarVacaciones.toLowerCase();
                  return (
                    (emp.numero_legajo && emp.numero_legajo.toString().toLowerCase().includes(termino)) ||
                    (emp.dni && emp.dni.toString().toLowerCase().includes(termino)) ||
                    (emp.apellido && emp.apellido.toLowerCase().includes(termino)) ||
                    (emp.nombre && emp.nombre.toLowerCase().includes(termino))
                  );
                }).length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron empleados
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de Inicio"
                InputLabelProps={{ shrink: true }}
                value={nuevaVacacion.fecha_inicio}
                onChange={(e) => setNuevaVacacion({ ...nuevaVacacion, fecha_inicio: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de Fin"
                InputLabelProps={{ shrink: true }}
                value={nuevaVacacion.fecha_fin}
                onChange={(e) => setNuevaVacacion({ ...nuevaVacacion, fecha_fin: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comentarios"
                value={nuevaVacacion.comentarios}
                onChange={(e) => setNuevaVacacion({ ...nuevaVacacion, comentarios: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCargarVacaciones}
                disabled={loading}
              >
                {loading ? 'Cargando...' : 'Cargar Vacaciones'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* TAB 2: TODAS LAS SOLICITUDES */}
      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Todas las Solicitudes</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtroEstado}
                  label="Estado"
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendiente_referente">Pendiente Referente</MenuItem>
                  <MenuItem value="pendiente_rh">Pendiente RH</MenuItem>
                  <MenuItem value="aprobado">Aprobado</MenuItem>
                  <MenuItem value="rechazado_referente">Rechazado Referente</MenuItem>
                  <MenuItem value="rechazado_rh">Rechazado RH</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                  <MenuItem value="suspendido">Suspendido</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Año</InputLabel>
                <Select
                  value={filtroAnio}
                  label="Año"
                  onChange={(e) => setFiltroAnio(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 3 + i).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton onClick={cargarSolicitudes} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Buscador */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por ID, legajo, DNI o apellido"
              value={busquedaSolicitudes}
              onChange={(e) => setBusquedaSolicitudes(e.target.value)}
              placeholder="Ingrese ID, legajo, DNI o apellido..."
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Empleado</TableCell>
                    <TableCell>Fecha Inicio</TableCell>
                    <TableCell>Fecha Fin</TableCell>
                    <TableCell align="center">Días</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Referente</TableCell>
                    <TableCell>RH</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solicitudes
                    .filter(sol => {
                      if (!busquedaSolicitudes) return true;
                      const termino = busquedaSolicitudes.toLowerCase();
                      return (
                        (sol.id && sol.id.toString().includes(termino)) ||
                        (sol.legajo && sol.legajo.toString().toLowerCase().includes(termino)) ||
                        (sol.dni && sol.dni.toString().toLowerCase().includes(termino)) ||
                        (sol.apellido && sol.apellido.toLowerCase().includes(termino)) ||
                        (sol.nombre && sol.nombre.toLowerCase().includes(termino))
                      );
                    })
                    .map((sol) => (
                    <TableRow key={sol.id}>
                      <TableCell>{sol.id}</TableCell>
                      <TableCell>
                        {sol.apellido}, {sol.nombre}
                        <Typography variant="caption" display="block" color="text.secondary">
                          DNI: {sol.dni}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatearFecha(sol.fecha_inicio)}</TableCell>
                      <TableCell>{formatearFecha(sol.fecha_fin)}</TableCell>
                      <TableCell align="center">{sol.dias_solicitados}</TableCell>
                      <TableCell>{getEstadoChip(sol.estado, esAjusteAdministrativo(sol))}</TableCell>
                      <TableCell>
                        {sol.referente_nombre ? (
                          <>
                            {sol.referente_apellido}, {sol.referente_nombre}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatearFecha(sol.fecha_referente)}
                            </Typography>
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {sol.rh_nombre ? (
                          <>
                            {sol.rh_apellido}, {sol.rh_nombre}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatearFecha(sol.fecha_rh)}
                            </Typography>
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            onClick={() => handleVerDetalle(sol)}
                          >
                            Ver Detalle
                          </Button>
                          {sol.estado === 'aprobado' && (
                            <>
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={() => handleAbrirSuspender(sol)}
                              >
                                Suspender
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleAbrirCancelar(sol)}
                              >
                                Cancelar
                              </Button>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* DIALOG: EDITAR DÍAS DISPONIBLES */}
      <Dialog open={openEditarDias} onClose={() => setOpenEditarDias(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajustar Días Disponibles - {empleadoSeleccionado?.apellido}, {empleadoSeleccionado?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Días disponibles actuales: <strong>{empleadoSeleccionado?.dias_disponibles}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Ingresa un valor positivo para agregar días, o negativo para quitar días.
            </Typography>

            <TextField
              fullWidth
              type="number"
              label="Días a ajustar"
              value={diasAjuste}
              onChange={(e) => setDiasAjuste(e.target.value)}
              helperText={`Nuevo total: ${(Number(empleadoSeleccionado?.dias_disponibles) || 0) + (Number(diasAjuste) || 0)} días`}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motivo del ajuste"
              value={motivoAjuste}
              onChange={(e) => setMotivoAjuste(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditarDias(false)}>Cancelar</Button>
          <Button
            onClick={handleGuardarAjusteDias}
            variant="contained"
            disabled={!motivoAjuste || !diasAjuste || Number(diasAjuste) === 0 || loading}
          >
            {loading ? 'Guardando...' : 'Guardar Ajuste'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: DETALLE DE SOLICITUD */}
      <Dialog
        open={openDetalleSolicitud}
        onClose={() => setOpenDetalleSolicitud(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle de Solicitud #{solicitudDetalle?.id}</DialogTitle>
        <DialogContent>
          {solicitudDetalle && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Empleado</Typography>
                  <Typography variant="body1">{solicitudDetalle.apellido}, {solicitudDetalle.nombre}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">DNI</Typography>
                  <Typography variant="body1">{solicitudDetalle.dni}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Fecha Inicio</Typography>
                  <Typography variant="body1">{formatearFecha(solicitudDetalle.fecha_inicio)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Fecha Fin</Typography>
                  <Typography variant="body1">{formatearFecha(solicitudDetalle.fecha_fin)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Días Solicitados</Typography>
                  <Typography variant="body1">{solicitudDetalle.dias_solicitados}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                  {getEstadoChip(solicitudDetalle.estado, esAjusteAdministrativo(solicitudDetalle))}
                </Grid>
                {solicitudDetalle.comentarios_empleado && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Comentarios del Empleado</Typography>
                    <Typography variant="body1">{solicitudDetalle.comentarios_empleado}</Typography>
                  </Grid>
                )}
                {solicitudDetalle.referente_comentario && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Comentario del Referente ({solicitudDetalle.referente_apellido}, {solicitudDetalle.referente_nombre})
                    </Typography>
                    <Typography variant="body1">{solicitudDetalle.referente_comentario}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatearFecha(solicitudDetalle.fecha_referente)}
                    </Typography>
                  </Grid>
                )}
                {solicitudDetalle.rh_comentario && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Comentario de RH ({solicitudDetalle.rh_apellido}, {solicitudDetalle.rh_nombre})
                    </Typography>
                    <Typography variant="body1">{solicitudDetalle.rh_comentario}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatearFecha(solicitudDetalle.fecha_rh)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetalleSolicitud(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: CANCELAR SOLICITUD APROBADA */}
      <Dialog open={openCancelarSolicitud} onClose={() => setOpenCancelarSolicitud(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cancelar vacaciones - {solicitudACancelar?.apellido}, {solicitudACancelar?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Se van a devolver los <strong>{solicitudACancelar?.dias_solicitados} días</strong> al saldo
              del empleado (período {formatearFecha(solicitudACancelar?.fecha_inicio)} al{' '}
              {formatearFecha(solicitudACancelar?.fecha_fin)}). Usá esto cuando se cancela antes de
              haber empezado a gozarse. Si ya arrancó y hay que devolver solo parte de los días, usá
              "Suspender" en vez de esto.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motivo de la cancelación"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelarSolicitud(false)} disabled={loading}>Volver</Button>
          <Button
            onClick={handleConfirmarCancelar}
            variant="contained"
            color="error"
            disabled={loading || !motivoCancelacion.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Cancelar vacaciones'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: SUSPENDER SOLICITUD APROBADA */}
      <Dialog open={openSuspenderSolicitud} onClose={() => setOpenSuspenderSolicitud(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Suspender vacaciones - {solicitudASuspender?.apellido}, {solicitudASuspender?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Usá esto cuando la vacación ya empezó y hay que cortarla por algún problema. Solo se
              devuelven los días que <strong>NO</strong> llegó a gozar.
            </Alert>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Días originalmente solicitados: <strong>{solicitudASuspender?.dias_solicitados}</strong>
              {' '}(período {formatearFecha(solicitudASuspender?.fecha_inicio)} al{' '}
              {formatearFecha(solicitudASuspender?.fecha_fin)})
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Días a devolver (no gozados)"
              value={diasADevolver}
              onChange={(e) => setDiasADevolver(e.target.value)}
              inputProps={{ min: 1, max: solicitudASuspender?.dias_solicitados || undefined }}
              sx={{ mb: 2, mt: 1 }}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motivo de la suspensión"
              value={motivoSuspension}
              onChange={(e) => setMotivoSuspension(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSuspenderSolicitud(false)} disabled={loading}>Volver</Button>
          <Button
            onClick={handleConfirmarSuspender}
            variant="contained"
            color="warning"
            disabled={loading || !motivoSuspension.trim() || !diasADevolver}
          >
            {loading ? <CircularProgress size={24} /> : 'Suspender vacaciones'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PanelAdmin;
